'use-client';
import * as React from 'react';
import {
  formatClassNames,
  getDataAttributes,
} from '@wix/editor-elements-common-utils';
import ResponsiveContainer from '@wix/thunderbolt-elements/components/ResponsiveContainer';
import classNames from 'clsx';
import { HamburgerMenuContextProvider } from '../../HamburgerMenuContext';
import type { IHamburgerMenuRootProps } from '../HamburgerMenuRoot.props';
import classes from './style/HamburgerMenuRoot.scss';
import { useAnimationState } from '../../../Menu/viewer/components/MenuItem/useAnimationState';
import {
  INITIAL_HAMBURGER_MENU_ANIMATION_STATE,
  HAMBURGER_MENU_ANIMATION_CONFIG,
  ANIMATION_PACKAGE_CONFIG,
} from '../../../Menu/viewer/constants';
import { getAnimationPackage } from '../../../Menu/viewer/utils/animationUtils';
import { useMenuContext } from '../../../Menu/viewer/MenuContext';
import { HamburgerMenuAnimationName } from '../../../Menu/constants';

const HamburgerMenuRoot: React.FC<IHamburgerMenuRootProps> = props => {
  const {
    id,
    className,
    customClassNames = [],
    children,
    compPreviewState,
    containerProps,
    containerRootClassName,
    hasResponsiveLayout,
    shouldFocus,
    isMenuOpen,
    updateComponentPropsInViewer,
    isClassNameToRootEnabled,
    isCssPreHydrationEnabled,
  } = props;

  const { setIsHamburgerMenuOpened } = useMenuContext();

  const {
    animationState,
    initEnterAnimation,
    initExitAnimation,
    skipToEnterDone,
  } = useAnimationState(
    HAMBURGER_MENU_ANIMATION_CONFIG,
    INITIAL_HAMBURGER_MENU_ANIMATION_STATE,
  );

  const prehydrationToggleId = isCssPreHydrationEnabled
    ? `${id}-toggle`
    : undefined;
  const checkboxRef = React.useRef<HTMLInputElement>(null);

  // Compute the hamburger animation name for the CSS pre-hydration data attribute.
  // On SSR (typeof document === 'undefined'), fall back to None since we can't read
  // computed styles. The CSS rules use this attribute to select the right animation.
  const prehydrationAnimationName = React.useMemo(() => {
    if (!isCssPreHydrationEnabled) {
      return HamburgerMenuAnimationName.None;
    }
    if (typeof document === 'undefined') {
      return HamburgerMenuAnimationName.None;
    }
    const animationPackage = getAnimationPackage(id);
    return ANIMATION_PACKAGE_CONFIG[animationPackage]
      .hamburgerMenuAnimationName;
  }, [isCssPreHydrationEnabled, id]);

  // Hydration sync: if the user toggled the checkbox before React hydrated,
  // sync the checked state into React state.
  React.useEffect(() => {
    if (checkboxRef.current?.checked && !isMenuOpen) {
      updateComponentPropsInViewer({ isMenuOpen: true });
      skipToEnterDone(prehydrationAnimationName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Remove pre-hydration attribute after React hydrates to restore normal
  // button click behavior and disable the CSS pre-hydration overlay labels.
  const rootRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    // If menu was opened during pre-hydration, defer removal until React state
    // syncs (see isMenuOpen effect below) to avoid a visual flash.
    if (!checkboxRef.current?.checked) {
      rootRef.current?.removeAttribute('data-prehydration');
    }
  }, []);

  React.useEffect(() => {
    if (isMenuOpen && rootRef.current?.hasAttribute('data-prehydration')) {
      // Double rAF: first rAF fires before the next paint, second fires after
      // one full frame has been painted. This ensures React's state update from
      // the hydration sync has been committed to the DOM before we remove the
      // CSS pre-hydration fallback rules.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          rootRef.current?.removeAttribute('data-prehydration');
        });
      });
    }
  }, [isMenuOpen]);

  const { shouldOmitWrapperLayers } = containerProps ?? {};

  const childrenToRender =
    typeof children === 'function' ? children : () => children;

  React.useEffect(() => {
    if (typeof isMenuOpen !== 'undefined' && setIsHamburgerMenuOpened) {
      setIsHamburgerMenuOpened?.(isMenuOpen);
    }
  }, [isMenuOpen, setIsHamburgerMenuOpened]);

  const setIsMenuOpen = React.useCallback(
    (isOpen: boolean) => {
      if (isMenuOpen === isOpen) {
        return;
      }

      const getHamburgerMenuAnimationName = () => {
        const animationPackage = getAnimationPackage(id);
        const hamburgerMenuAnimationName =
          ANIMATION_PACKAGE_CONFIG[animationPackage].hamburgerMenuAnimationName;

        return hamburgerMenuAnimationName;
      };

      const hamburgerMenuAnimationName = getHamburgerMenuAnimationName();
      if (isOpen) {
        updateComponentPropsInViewer({ isMenuOpen: true });
        initEnterAnimation(hamburgerMenuAnimationName);
      } else {
        initExitAnimation(hamburgerMenuAnimationName, () =>
          updateComponentPropsInViewer({ isMenuOpen: false }),
        );
      }
    },
    [
      isMenuOpen,
      updateComponentPropsInViewer,
      id,
      initEnterAnimation,
      initExitAnimation,
    ],
  );

  return (
    <div
      ref={rootRef}
      id={id}
      {...getDataAttributes(props)}
      data-prehydration={isCssPreHydrationEnabled ? '' : undefined}
      className={classNames(
        // Semantic classes are applied in HamburgerOpenButton
        formatClassNames(null, ...customClassNames),
        isClassNameToRootEnabled && className,
        containerRootClassName,
        shouldOmitWrapperLayers && classes.root,
      )}
    >
      {prehydrationToggleId && (
        <input
          type="checkbox"
          id={prehydrationToggleId}
          ref={checkboxRef}
          data-hamburger-toggle=""
          data-hamburger-animation={prehydrationAnimationName}
          aria-hidden="true"
          tabIndex={-1}
          autoComplete="off"
          className={classes.hiddenToggle}
        />
      )}
      <HamburgerMenuContextProvider
        compPreviewState={compPreviewState}
        shouldFocus={shouldFocus}
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
        animationState={animationState}
        prehydrationToggleId={prehydrationToggleId}
      >
        {hasResponsiveLayout ? (
          <ResponsiveContainer
            {...containerProps!}
            tabIndex={-1}
            extraRootClass={classes.root}
          >
            {childrenToRender}
          </ResponsiveContainer>
        ) : (
          childrenToRender()
        )}
      </HamburgerMenuContextProvider>
    </div>
  );
};

export default HamburgerMenuRoot;
